import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, brl } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Gavel, Loader2, Sparkles, MapPin, FileText, Building2, Scale, Heart, Share2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setData(r.data)).catch((e) => {
      toast.error("Veículo não encontrado");
      navigate("/search");
    });
    api.get("/favorites").then((r) => setIsFav((r.data.ids || []).includes(id))).catch(() => {});
  }, [id, navigate]);

  const toggleFav = async () => {
    try {
      if (isFav) {
        await api.delete(`/favorites/${id}`);
        setIsFav(false);
        toast.success("Removido dos favoritos");
      } else {
        await api.post("/favorites", { vehicle_id: id });
        setIsFav(true);
        toast.success("Adicionado aos favoritos");
      }
    } catch { toast.error("Falha ao atualizar favoritos"); }
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/analyze/${id}`);
      setAi(data);
    } catch {
      toast.error("Falha ao gerar análise de IA");
    } finally { setAiLoading(false); }
  };

  const [fipeLoading, setFipeLoading] = useState(false);
  const refreshFipe = async () => {
    setFipeLoading(true);
    try {
      const { data } = await api.post(`/fipe/refresh/${id}`);
      setData((d) => ({ ...d, vehicle: data.vehicle }));
      toast.success(`FIPE atualizada: ${data.fipe.ref_month}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "FIPE indisponível";
      toast.error(typeof msg === "string" ? msg : "Erro FIPE");
    } finally { setFipeLoading(false); }
  };

  const shareWhats = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const shareUrl = `${backendUrl}/api/og/${id}`;
    const v = data.vehicle;
    const text = `🔎 *${v.brand} ${v.model} ${v.year}* — leilão judicial em ${v.uf}\n\n💰 Lance mínimo: R$ ${v.min_bid.toLocaleString("pt-BR")}\n📊 FIPE: R$ ${v.fipe_value.toLocaleString("pt-BR")}\n⭐ Score: ${v.analysis.score}/100${v.payment_25_30 ? "\n💳 25% entrada + 30 boletos" : ""}\n\n${shareUrl}`;
    api.post(`/share/${id}/track?channel=whatsapp`).catch(() => {});
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (!data) return (
    <div className="min-h-screen"><Header /><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div></div>
  );

  const v = data.vehicle;
  const a = v.analysis;
  const auc = data.auctioneer;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/search" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar à pesquisa
        </Link>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left */}
          <div className="lg:col-span-3 space-y-4">
            <div className="rounded-xl overflow-hidden bg-slate-900 border border-slate-800 relative">
              <img src={v.image} alt={v.model} className="w-full h-72 sm:h-96 object-cover" />
              <button onClick={toggleFav} className={`absolute top-3 right-3 w-11 h-11 rounded-full flex items-center justify-center transition-colors ${isFav ? "bg-red-500/25 border border-red-500/60" : "bg-black/60 border border-slate-700 hover:bg-red-500/20"}`} data-testid="favorite-toggle">
                <Heart className={`w-5 h-5 ${isFav ? "text-red-400 fill-red-400" : "text-slate-300"}`} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="deal-score">{a.score}/100 · {a.rating}</div>
              {v.payment_25_30 && <div className="badge-parcel"><Gavel className="w-3 h-3" />25% Entrada + 30 boletos</div>}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs bg-slate-800 border border-slate-700 text-slate-300"><MapPin className="w-3 h-3" />{v.uf} · {v.city}</div>
            </div>
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white" data-testid="vehicle-title">{v.brand} {v.model}</h1>
              <div className="mt-2 text-slate-400 text-sm">
                {v.year} · {v.color} · {v.km.toLocaleString("pt-BR")} km · Lote {v.lot_number}
              </div>
            </div>

            {/* AI section */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-emerald-500/10 to-slate-900 border border-emerald-500/25" data-testid="ai-analysis-card">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                    <h2 className="font-display text-lg font-bold text-white">Análise de IA</h2>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Powered by Gemini 3 Flash · gratuita neste lote</p>
                </div>
                {!ai && (
                  <Button onClick={runAi} disabled={aiLoading} className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" data-testid="ai-generate-btn">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gerar análise"}
                  </Button>
                )}
              </div>
              {ai && (
                <div className="mt-4 whitespace-pre-wrap text-sm text-slate-200 leading-relaxed" data-testid="ai-analysis-text">
                  {ai.analysis}
                  {ai.fallback && <div className="mt-3 text-xs text-amber-400/80">⚠ Análise em modo offline (IA indisponível).</div>}
                </div>
              )}
            </div>

            {/* Process info */}
            <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800">
              <h3 className="font-display text-sm font-bold text-white flex items-center gap-2"><Scale className="w-4 h-4 text-slate-400" />Informações do processo</h3>
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><dt className="text-xs text-slate-500 uppercase tracking-widest">Vara</dt><dd className="text-slate-200">{v.court}</dd></div>
                <div><dt className="text-xs text-slate-500 uppercase tracking-widest">Processo</dt><dd className="font-mono text-slate-200 text-xs">{v.process_number}</dd></div>
                <div><dt className="text-xs text-slate-500 uppercase tracking-widest">Encerramento</dt><dd className="text-slate-200">{new Date(v.auction_end).toLocaleString("pt-BR")}</dd></div>
                <div><dt className="text-xs text-slate-500 uppercase tracking-widest">Edital</dt><dd><a href={v.edital_url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300 text-xs inline-flex items-center gap-1"><FileText className="w-3 h-3" />Baixar PDF</a></dd></div>
              </dl>
            </div>
          </div>

          {/* Right - price breakdown */}
          <div className="lg:col-span-2 space-y-4">
            <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800 sticky top-24" data-testid="price-breakdown">
              <h3 className="font-display text-sm font-bold text-white">Custo total estimado</h3>
              <div className="mt-4 space-y-2 text-sm">
                {[
                  ["Lance mínimo", v.min_bid],
                  [`Comissão leiloeiro (${v.commission_pct}%)`, a.commission_value],
                  ["Custas judiciais", v.judicial_fees],
                  ["Guinchamento/Pátio", v.yard_fees],
                  ["IPVA/débitos pendentes", v.ipva_debts],
                ].map(([k, val]) => (
                  <div key={k} className="flex justify-between py-1 border-b border-slate-800/60">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-mono text-slate-200">{brl(val)}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 mt-2">
                  <span className="font-bold text-white">Total investido</span>
                  <span className="font-mono text-lg font-bold text-white">{brl(a.total_cost)}</span>
                </div>
              </div>

              <div className="mt-4 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300">Valor de mercado (FIPE)</div>
                    <div className="font-mono text-2xl font-bold text-emerald-400 mt-1">{brl(v.fipe_value)}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={refreshFipe} disabled={fipeLoading} className="h-8 text-emerald-300 hover:bg-emerald-500/20" data-testid="fipe-refresh-btn">
                    {fipeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                <div className="mt-2 text-xs text-slate-300">
                  Economia estimada: <span className="font-mono font-bold text-emerald-400">{brl(a.savings)}</span> ({a.savings_pct}%)
                </div>
                {v.fipe_source === "parallelum" && (
                  <div className="mt-1 text-[10px] font-mono text-emerald-500/70">✓ FIPE oficial · {v.fipe_ref_month}</div>
                )}
              </div>

              <Button onClick={shareWhats} className="mt-4 w-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold" data-testid="detail-share-btn">
                <Share2 className="w-4 h-4 mr-2" />Compartilhar no WhatsApp
              </Button>

              {auc && (
                <div className="mt-4 p-4 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="flex items-center gap-2 text-xs text-slate-400"><Building2 className="w-3.5 h-3.5" />Leiloeiro Oficial</div>
                  <div className="mt-1 font-semibold text-white text-sm">{auc.name}</div>
                  <div className="text-xs text-slate-400">{auc.matricula} · {auc.junta}</div>
                  <a href={auc.website} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-emerald-400 hover:text-emerald-300" data-testid="auctioneer-website-link">
                    Acessar site oficial →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
